/**
 * Import React libraries.
 */
import * as React from 'react';
import {Size} from '../types';

/**
 * Import custom styles.
 */
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';
import {ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentXSStyling} from '../styles';
import {Z_INDEXES} from '../styles';
import {defaultOnMouseDownHandler} from '../common';
import Colors from "../styles/colors";

// Add custom styles
const HorizontalTabContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  flex-direction: row;

  &:focus {
    ${focusStyles};
  }

`;

const TabSideFill = styled.div`
  position: absolute;
  border-bottom: 1px solid ${props => COLORS.generateToken({ componentType:'border', defaultVariant:'subtle' }, props.theme)};
  bottom: 0;
  width: 100%;

  &.fill {
    background: ${props => COLORS.generateToken({ componentType: 'bg-surface', defaultVariant:'default' }, props.theme)};
  }
`;

const TabButton = styled.button`
  color: ${props => COLORS.generateToken({componentType: 'text', defaultVariant: 'subtle'}, props.theme)};
  border: none;
  background-color: transparent;
  position: relative;
  cursor: pointer;
  display: flex;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: 0;

  &[aria-selected="true"] {
    z-index: ${Z_INDEXES.active};
  }

  &.floating {
    border-radius: 2px;
  }
  
  &.selected {
    background-color: ${props => COLORS.generateToken({componentType: 'bg-surface', defaultVariant: 'default'}, props.theme)};
    color: ${props => COLORS.generateToken({componentType: 'text', defaultVariant: 'default'}, props.theme)};
  }

  &.selected.floating {
    background-color: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant: 'selected'}, props.theme)};
  }

  &.default {
    border-left: 1px solid transparent;
    border-right: 1px solid transparent;
  }
  
  &.default.selected {
    border-left: 1px solid ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'subtle' }, props.theme)};
    border-right: 1px solid ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'subtle' }, props.theme)};
  }

  &.selected:after {
    content: "";
    position: absolute;
    top: 0;
    left: -1px;
    right: -1px;
    height: 2px;
    background-color: ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'selected' }, props.theme)};
  }

  &.selected.floating:after {
    top: unset;
    bottom: 1px;
    left: 1px;
    right: 1px;
    height: 2px;
    border-radius: 2px;
    background-color: ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'selected' }, props.theme)};
  }

  &:hover, &:hover.selected {
    background: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'hover' }, props.theme)};
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'hover' }, props.theme)};
  }

  &:hover:after {
    background-color: ${props => COLORS.generateToken({ componentType:'border', state:'hover' }, props.theme)};
  }

  &:active, &:active.selected {
    background: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'active' }, props.theme)};
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'active' }, props.theme)};
    outline: none;
  }

  &:active:after {
    background-color: ${props => COLORS.generateToken({ componentType: 'border', state: 'active' }, props.theme)};
  }

  &:disabled {
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};
    background: transparent;
    cursor: not-allowed;
  }

  // Sizes
  &.${Size.XSmall} {
    ${ComponentXSStyling(ComponentTextStyle.Regular, null)}
    padding: 0px 16px;
    height: 32px;
    margin-top: 16px;
  }

  &.${Size.Small} {
    ${ComponentSStyling(ComponentTextStyle.Regular, null)}
    padding: 0px 16px;
    height: 40px;
    margin-top: 8px;
  }

  &.${Size.Medium} {
    ${ComponentMStyling(ComponentTextStyle.Regular, null)}
    height: 48px;
    min-width: 96px;
    padding: 0 16px;
  }

  &.${Size.Large} {
    ${ComponentLStyling(ComponentTextStyle.Regular, null)}
    height: 56px;
    min-width: 144px;
    padding: 0 16px;
  }
`;

// Add component-specific types
export interface HorizontalTabProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Required. Size of the Tabs */
  size: Size.XSmall | Size.Small | Size.Medium | Size.Large;
  /** Required: Array of Tabs to show */
  tabs: HorizontalTab[];
  /** Optional. For 'default' variant of tabs if this flag is set, then white background will be added to the tabs  */
  sideFill?: boolean;
  /** Optional. If set, then whole width of the parent container will be equally split between Tabs, otherwise each tab will take width 
   * required for its content.
   */
  fullWidth?: boolean;
  /** Required. Handler to be called when user tries to change the tab */
  onTabChange: (to: string) => void;
  /** Optional. Display variant of the tabs. If not set, then 'default' is used */
  variant?: 'default' | 'floating';
}

export interface HorizontalTab extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'tabIndex' | 'onMouseDown' | 'onClick' | 'style'> {
  /** Required. Value of the tab */
  value: string | React.ReactNode;
  /** Required. State of the tab, selected or not. Use it to set initial state of the tabs. */
  selected: boolean;
  /** Required. URL related with the Tab */
  to: string;
}

const HorizontalTabs = ({size, tabs, sideFill, fullWidth, onTabChange, variant, ...rest}: HorizontalTabProps) => {
  /**
   * Informs parent component of tab change and clears focus.
   * @param to - A link to which user should be navigated.
   */
  const doTabChange = (to: string): void => {
    // Let's inform parent component
    onTabChange(to);

    // Let's clear focus
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement!.blur();
    }
  };

  const [focused, setFocused] = React.useState<boolean>(false);
  const [currentTab, setCurrentTab] = React.useState<string>();

  React.useEffect(() => {
    const selectedTab = tabs.find(x => x.selected && !x.disabled);
    if (selectedTab)
      setCurrentTab(selectedTab.to);
  }, [tabs]);

  const tabListBlur = () => setFocused(false);

  const tabListFocus = (e: React.FocusEvent<HTMLDivElement, Element>) => setFocused(true);

  const changeTabInternal = (tab: HorizontalTab) => {
    tab.selected = true;
    tabs.forEach(x => {
      if (x.to != tab.to)
        x.selected = false;
    });
    setCurrentTab(tab.to);
    onTabChange(tab.to);
  }

  const tabListKeyDown = (e: React.KeyboardEvent) => {
    if (focused && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      if (e.key === 'ArrowRight') {
        tabs.forEach((x, i) => {
          if (x.to != currentTab)
            return;

          for (let j = 1; j < tabs.length - 1; j++) {
            let nextIndex = j + i;
            if (nextIndex > tabs.length - 1)
              nextIndex = nextIndex - tabs.length;
            if (tabs[nextIndex].disabled)
              continue;
            changeTabInternal(tabs[nextIndex]);
            break;
          }
        });
      }

      if (e.key === 'ArrowLeft') {
        tabs.forEach((x, i) => {
          if (x.to != currentTab)
            return;

          for (let j = 1; j < tabs.length - 1; j++) {
            let nextIndex = i - j;
            if (nextIndex < 0)
              nextIndex = nextIndex + tabs.length;
            if (tabs[nextIndex].disabled)
              continue;
            changeTabInternal(tabs[nextIndex]);
            break;
          }
        });
      }
    }
  };

  return (
    <HorizontalTabContainer tabIndex={0} onBlur={tabListBlur} onFocus={tabListFocus} onKeyDown={tabListKeyDown}
                            role="tablist" data-testid={'horizontal-tabs-container'} {...rest}>
      {
        variant !== 'floating' && <TabSideFill tabIndex={-1} className={`${sideFill ? 'fill' : ''}`}/>
      }
      {tabs.map((tab: HorizontalTab, index: number) => {
        const {value, selected, to, disabled, className, ...rest} = tab;
        return (
          <TabButton
            role="tab"
            data-testid={`tab_${value}`}
            key={`${tab}_${index}`}
            aria-selected={selected}
            aria-expanded={selected}
            tabIndex={-1}
            onMouseDown={defaultOnMouseDownHandler}
            style={fullWidth ? {width: `${100 / tabs.length}%`} : {}}
            disabled={disabled}
            className={`${className || ''} ${size} ${variant || 'default'} ${tab.selected ? 'selected' : ''}`}
            onClick={() => !disabled && changeTabInternal(tab)}
            {...rest}>
            {tab.value}
          </TabButton>
        );
      })}
    </HorizontalTabContainer>
  );
};

export default HorizontalTabs;
